import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TextField, MenuItem, Stack, Button, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { toYyyyMmDd } from '../../utils/businessDays';
import StatusChip from '../StatusChip';
import EmptyState from '../ui/EmptyState';
import ErrorBanner from '../ui/ErrorBanner';
import { usePermissions } from '../PermissionsContext';
import { handleApiError, handleApiSuccess } from '../Common/ApiErrorHandler';

export default function SickLeaveReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [sickLeaves, setSickLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { isSystemAdmin } = usePermissions();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/leave-requests/sick-leave-report', { params: { year, month } });
      // Backend returns { Year, Month, DepartmentId, Summary, Requests }
      const requests = data?.requests || data?.Requests || (Array.isArray(data) ? data : []);
      setSickLeaves(requests);
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const totalDays = sickLeaves.reduce((acc, sl) => acc + (sl.totalDays || sl.TotalDays || 0), 0);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/leave-requests/${deleteConfirm.id}`);
      handleApiSuccess(`Sick leave for ${deleteConfirm.employeeName} deleted successfully`);
      setDeleteConfirm(null);
      load();
    } catch (err) {
      handleApiError(err, 'Failed to delete sick leave');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await api.get('/leave-requests/sick-leave-report/export', {
        params: { year, month },
        responseType: 'blob'
      });

      if (!blob || !(blob instanceof Blob)) {
        setError('No data received from server. Please try again.');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Sick_Leave_Report_${year}_${month.toString().padStart(2, '0')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to export report');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Sick Leave Report</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          select
          label="Year"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 100 }}
        >
          {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField
          select
          label="Month"
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 140 }}
        >
          {monthOptions.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={load} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Refresh'}
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={loading || sickLeaves.length === 0}
        >
          Export to CSV
        </Button>
      </Stack>

      {error && <ErrorBanner message={error} onClose={() => setError('')} />}

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : sickLeaves.length === 0 ? (
          <EmptyState title="No sick leaves" description={`No sick leaves reported for ${monthOptions.find(m => m.value === month)?.label} ${year}`} />
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Total: {sickLeaves.length} sick leave(s), {totalDays} day(s)
            </Typography>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Status</TableCell>
                    {isSystemAdmin && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sickLeaves.map(sl => {
                    const id = sl.id || sl.Id;
                    const startYmd = (sl.startDateLocalYmd || sl.StartDateLocalYmd) ?? toYyyyMmDd(new Date(sl.startDate || sl.StartDate));
                    const endYmd = (sl.endDateLocalYmd || sl.EndDateLocalYmd) ?? toYyyyMmDd(new Date(sl.endDate || sl.EndDate));
                    const empName = sl.employeeName || sl.EmployeeName || `Employee #${sl.employeeId || sl.EmployeeId}`;
                    const deptName = sl.departmentName || sl.DepartmentName || '-';
                    return (
                      <TableRow key={id} hover>
                        <TableCell>{empName}</TableCell>
                        <TableCell>{deptName}</TableCell>
                        <TableCell>{startYmd}</TableCell>
                        <TableCell>{endYmd}</TableCell>
                        <TableCell align="right">{sl.totalDays || sl.TotalDays || sl.businessDays || sl.BusinessDays || '-'}</TableCell>
                        <TableCell>{sl.notes || sl.Notes || sl.reason || sl.Reason || '-'}</TableCell>
                        <TableCell><StatusChip status={sl.status || sl.Status || 'Approved'} /></TableCell>
                        {isSystemAdmin && (
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteConfirm({ id, employeeName: empName, startDate: startYmd, endDate: endYmd })}
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Sick Leave</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the sick leave for <strong>{deleteConfirm?.employeeName}</strong> ({deleteConfirm?.startDate} to {deleteConfirm?.endDate})?
            <br /><br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

